/**
 * Reviews Section Component
 * Complete reviews section with header, tabs, list, and pagination
 */

import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '@/lib/i18n/client';
import { useReviews, useVoteReview, useReviewStats } from '@/lib/hooks/use-reviews';
import { StarRating } from './StarRating';
import { ReviewCard } from './ReviewCard';
import { WriteReviewModal } from './WriteReviewModal';

// Styled Components
const SectionContainer = styled.section`
  max-width: 900px;
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.space['2xl']} ${theme.space.md}`};
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  margin-bottom: ${({ theme }) => theme.space['2xl']};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatsHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.space['2xl']};
`;

const RatingDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const RatingNumber = styled.span`
  font-size: 48px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ReviewCount = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CustomerPhotos = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  margin: ${({ theme }) => theme.space.lg} 0;
  flex-wrap: wrap;
  justify-content: center;
`;

const CustomerPhoto = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.regular};
  cursor: pointer;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  margin: ${({ theme }) => theme.space.lg} 0;
`;

const WriteReviewButton = styled.button`
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space['2xl']};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background-color: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.primary};
  border: none;
  border-radius: ${({ theme }) => theme.radius.regular};
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const TabsContainer = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  margin-bottom: ${({ theme }) => theme.space.md};
`;

const TabsList = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.xl};
`;

const Tab = styled.button<{ active?: boolean }>`
  padding: ${({ theme }) => `${theme.space.md} 0`};
  font-size: 14px;
  font-weight: ${({ active }) => active ? 600 : 400};
  color: ${({ active, theme }) => active ? theme.colors.text.primary : theme.colors.text.secondary};
  background: none;
  border: none;
  border-bottom: 2px solid ${({ active, theme }) => active ? theme.colors.primary : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const SortContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${({ theme }) => theme.space.md};
`;

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.md}`};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  cursor: pointer;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ReviewsList = styled.div`
  margin-top: ${({ theme }) => theme.space.md};
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  margin-top: ${({ theme }) => theme.space['2xl']};
`;

const PageButton = styled.button<{ active?: boolean }>`
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: ${({ active }) => active ? 600 : 400};
  color: ${({ active, theme }) => active ? theme.colors.text.primary : theme.colors.text.secondary};
  background: none;
  border: none;
  cursor: pointer;
  
  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ArrowButton = styled(PageButton)`
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Ellipsis = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.space['2xl']};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.space['2xl']};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

interface ReviewsSectionProps {
  productId: string;
}

export function ReviewsSection({ productId }: ReviewsSectionProps) {
  const { t } = useTranslation('pdp');
  const [currentPage, setCurrentPage] = useState(1);
  const [isReviewDrawerOpen, setIsReviewDrawerOpen] = useState(false);
  const pageSize = 5;

  const { data: reviewsData, isLoading: reviewsLoading } = useReviews(productId, {
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    includeStats: true,
  });

  const shouldFetchFallbackStats = Boolean(
    reviewsData &&
    !reviewsData.stats &&
    ((reviewsData.total ?? 0) > 0 || reviewsData.reviews.length > 0),
  );
  const { data: stats } = useReviewStats(productId, shouldFetchFallbackStats);
  const voteReviewMutation = useVoteReview();

  const handleVote = useCallback((reviewId: string, voteType: 'helpful' | 'not_helpful') => {
    voteReviewMutation.mutate({ reviewId, voteType });
  }, [voteReviewMutation]);

  const totalPages = reviewsData ? Math.ceil(reviewsData.total / pageSize) : 0;

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages: (number | 'ellipsis')[] = [];
    
    // Always show first page
    pages.push(1);
    
    if (currentPage > 3) {
      pages.push('ellipsis');
    }
    
    // Show pages around current
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }
    
    if (currentPage < totalPages - 2) {
      pages.push('ellipsis');
    }
    
    // Always show last page
    if (totalPages > 1 && !pages.includes(totalPages)) {
      pages.push(totalPages);
    }

    return (
      <PaginationContainer>
        <ArrowButton
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </ArrowButton>

        {pages.map((page, idx) => (
          page === 'ellipsis' ? (
            <Ellipsis key={`ellipsis-${idx}`}>...</Ellipsis>
          ) : (
            <PageButton
              key={page}
              active={page === currentPage}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </PageButton>
          )
        ))}

        <ArrowButton
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </ArrowButton>
      </PaginationContainer>
    );
  };

  const displayStats = reviewsData?.stats || stats;
  const averageRating = displayStats?.averageRating ?? 0;
  const totalReviews = displayStats?.totalReviews ?? reviewsData?.total ?? 0;

  // Extract images from reviews that have customerImage
  const reviewImages = reviewsData?.reviews
    .filter(review => review.customerImage)
    .map(review => ({
      id: review.id,
      url: review.customerImage as string,
      title: review.title,
    })) ?? [];

  return (
    <SectionContainer id="reviews-section">
      <SectionTitle>{t('reviews.title')}</SectionTitle>

      {/* Stats Header */}
      <StatsHeader>
        <RatingDisplay>
          <RatingNumber>{averageRating.toFixed(1)}</RatingNumber>
          <StarRating rating={averageRating} size="lg" />
        </RatingDisplay>
        <ReviewCount>{t('reviews.count', { count: totalReviews })}</ReviewCount>

        {/* Customer Photos Gallery - Shows images from actual reviews */}
        {reviewImages.length > 0 && (
          <CustomerPhotos>
            {reviewImages.map((img) => (
              <CustomerPhoto 
                key={img.id} 
                src={img.url} 
                alt={t('reviews.photoAlt', { title: img.title })}
                title={img.title}
              />
            ))}
          </CustomerPhotos>
        )}

        {/* Action Button */}
        <ActionButtons>
          <WriteReviewButton onClick={() => setIsReviewDrawerOpen(true)}>
            {t('reviews.writeReview')}
          </WriteReviewButton>
        </ActionButtons>
      </StatsHeader>

      {/* Tabs */}
      <TabsContainer>
        <TabsList>
          <Tab active={true}>
            {t('reviews.tab', { count: totalReviews })}
          </Tab>
        </TabsList>
      </TabsContainer>

      {/* Sort */}
      <SortContainer>
        <SortButton>
          {t('reviews.sort')}
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 10l5 5 5-5z" />
          </svg>
        </SortButton>
      </SortContainer>

      {/* Reviews List */}
      {reviewsLoading ? (
        <LoadingState>{t('reviews.loading')}</LoadingState>
      ) : reviewsData?.reviews.length === 0 ? (
        <EmptyState>{t('reviews.empty')}</EmptyState>
      ) : (
        <ReviewsList>
          {reviewsData?.reviews.map(review => (
            <ReviewCard
              key={review.id}
              review={review}
              onVote={handleVote}
            />
          ))}
        </ReviewsList>
      )}

      {/* Pagination */}
      {renderPagination()}

      <WriteReviewModal
        isOpen={isReviewDrawerOpen}
        onClose={() => setIsReviewDrawerOpen(false)}
        productId={productId}
      />
    </SectionContainer>
  );
}
